import { t } from "~/i18n";

/**
 * What the sign-out surface does after the attempt. A failure keeps the user
 * where they are: the Dovod session is still live, and moving them to the
 * sign-in card would show a surface they cannot get past.
 */
export type SignOutOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly message: string };

/**
 * The four effects a sign-out has, injected so the ordering contract below is
 * assertable without a router, a live platform-api, or a local server.
 */
export interface SignOutFlow {
  /**
   * Ends the Dovod session and drops the inference credential in both places
   * one is held (`~/shared/auth`'s wrapper, not Better Auth's bare call).
   * Reports failure either way Better Auth can: a returned `error`, or a
   * rejection when the request never completed.
   */
  readonly signOut: () => Promise<{ readonly error?: { readonly code?: string | null } | null }>;
  /**
   * Puts the server's inference credential back. The wrapper drops it before it
   * knows whether the session actually ended, so a failed sign-out would
   * otherwise leave a signed-in user whose next turn has no key to spend.
   */
  readonly restoreInferenceCredential: () => Promise<void>;
  /** Drops the memoized auth gate state and the in-memory server token. */
  readonly invalidateGateState: () => void;
  readonly navigateToSignIn: () => void;
}

/**
 * Invalidating before navigating is the whole contract.
 *
 * The root route re-runs `resolveInitialServerAuthGateState()` on every
 * navigation and answers from the memo while it reads "authenticated", so a
 * navigation that goes first arrives at `/sign-in` with the shell still
 * believing the session is good — and `SessionRevocationWatcher`, which is
 * mounted exactly then and still holding a valid server token, reads that as a
 * connection blip and sends the user straight back into the app.
 */
export async function runSignOut(flow: SignOutFlow): Promise<SignOutOutcome> {
  try {
    const { error } = await flow.signOut();
    if (error) {
      return await signOutFailed(flow);
    }
  } catch {
    return await signOutFailed(flow);
  }

  flow.invalidateGateState();
  flow.navigateToSignIn();
  return { ok: true };
}

async function signOutFailed(flow: SignOutFlow): Promise<SignOutOutcome> {
  await flow.restoreInferenceCredential();
  return { ok: false, message: t("settings/couldNotSignOut_v04ecs") };
}
